import { PERMISSIONS } from './permissions.js';
/**
 * The admin portal domain. §6.35 `ADM-001` to `ADM-009`; §6.37 `OPS-200` to `OPS-210`.
 *
 * Rules only, no endpoints. A lifecycle action names the single edge it takes, `from` and `to`,
 * because the target status alone does not identify an edge: three statuses reach `active`, so
 * keying on `to` lets an instance-move completion unsuspend a suspended tenant and lets a
 * reactivation mark a half-provisioned database live. Appendix C.12 and W.12 stay the authority,
 * and the test asserts every declared edge is in `TENANT_TRANSITIONS`, and that the only two
 * edges no operator action claims are the ones `TEN-002a` gives the provisioning job.
 *
 * **What an operator may not do is the design.** Appendix N lists "insider at ANUBRIS" as a threat
 * and `TEN-002` makes tenant isolation physical. The operator is therefore not a `Role`: it holds no
 * row of the Appendix B matrix, so every `Permission` is denied to it by default
 * (`operatorHoldsPermission`). The only road to a tenant's records is `ADM-005` impersonation, and
 * that road is gated on consent, a time box and a scope.
 *
 * **Allowlist.** `OPERATOR_ACTIONS` is the whole of what an operator can do. An action id that is
 * not in it is refused (`unknown_action`). Adding a capability means writing an entry here, with
 * the requirement id that justifies it, which is the point.
 *
 * **`ADM-009`.** Every evaluated action returns an audit entry carrying actor and reason, and a
 * blank reason is refused before anything else is checked. The actor kind is always
 * `platform_operator`, never `staff` (`ADM-005`, `decisions/0005`).
 *
 * Unspecified by the spec and taken as parameters or flagged: the impersonation time box length,
 * the impersonation scope, dunning schedule and grace period, usage warning thresholds, the
 * supported country list, the onboarding checklist items, the open export format, and the feature
 * flag schema (`SPEC-17`, OPEN.md). The denial codes below are this module's own, not Appendix F.
 */
// ---------------------------------------------------------------------------------------------
// The allowlist
// ---------------------------------------------------------------------------------------------
export const ADMIN_REQUIREMENT_IDS = [
    'ADM-001',
    'ADM-002',
    'ADM-003',
    'ADM-004',
    'ADM-005',
    'ADM-006',
    'ADM-007',
    'ADM-008',
    'ADM-009',
];
const NO_SCHEMA_RUN = [
    'provisioning',
    'provisioning_failed',
    'migrating',
    'purged',
];
export const OPERATOR_ACTIONS = [
    // ADM-001
    { id: 'tenant_list', requirement: 'ADM-001', kind: 'read_metadata' },
    { id: 'tenant_search', requirement: 'ADM-001', kind: 'read_metadata' },
    { id: 'tenant_inspect', requirement: 'ADM-001', kind: 'read_metadata' },
    {
        id: 'tenant_suspend',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'active',
        to: 'suspended',
    },
    {
        id: 'tenant_reactivate',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'suspended',
        to: 'active',
    },
    { id: 'plan_change', requirement: 'ADM-001', kind: 'mutate' },
    { id: 'trial_extend', requirement: 'ADM-001', kind: 'mutate' },
    { id: 'quota_adjust', requirement: 'ADM-001', kind: 'mutate' },
    { id: 'password_reset_force', requirement: 'ADM-001', kind: 'mutate' },
    // ADM-002
    { id: 'database_placement_view', requirement: 'ADM-002', kind: 'read_metadata' },
    { id: 'database_health_view', requirement: 'ADM-002', kind: 'read_metadata' },
    { id: 'restore_verification_view', requirement: 'ADM-002', kind: 'read_metadata' },
    { id: 'backup_on_demand', requirement: 'ADM-002', kind: 'mutate' },
    { id: 'restore_point_in_time', requirement: 'ADM-002', kind: 'mutate' },
    // ADM-003, ARC-020
    {
        id: 'migration_run',
        requirement: 'ADM-003',
        kind: 'mutate',
        excludedStatuses: NO_SCHEMA_RUN,
    },
    { id: 'migration_pause', requirement: 'ADM-003', kind: 'mutate' },
    { id: 'migration_resume', requirement: 'ADM-003', kind: 'mutate' },
    { id: 'migration_report_view', requirement: 'ADM-003', kind: 'read_metadata' },
    // ADM-004, DIR-005
    { id: 'capacity_view', requirement: 'ADM-004', kind: 'read_metadata' },
    {
        id: 'instance_move_begin',
        requirement: 'ADM-004',
        kind: 'lifecycle',
        from: 'active',
        to: 'migrating',
    },
    {
        id: 'instance_move_complete',
        requirement: 'ADM-004',
        kind: 'lifecycle',
        from: 'migrating',
        to: 'active',
    },
    // ADM-005
    { id: 'impersonate', requirement: 'ADM-005', kind: 'impersonate' },
    // ADM-006
    { id: 'platform_health_view', requirement: 'ADM-006', kind: 'read_metadata' },
    // ADM-007. The flag and country-pack halves only. The seller verification and moderation
    // queues serve the marketplace, which `V1-SPEC.md` section 2 puts out of v1, and an allowlist
    // entry is a granted capability, so they are not written here until that module exists.
    { id: 'feature_flag_set', requirement: 'ADM-007', kind: 'mutate' },
    { id: 'country_pack_enable', requirement: 'ADM-007', kind: 'mutate' },
    // ADM-008
    { id: 'billing_reconcile', requirement: 'ADM-008', kind: 'mutate' },
    /**
     * `OPS-209`. The read existed and nothing could write a limit, so "usage against plan limits" was
     * usage against nothing: `tenant_usage_limit` is read by `usage()` and was inserted by no file in
     * this repository.
     *
     * It is the operator's and not the shop's, because `AI-006` says the quota is "by plan" and a
     * plan is not a thing a shop sets for itself. `T-08` carries the AI credit numbers per plan and
     * nothing in the spec gives numbers for the other five kinds, so each one is set explicitly
     * rather than derived: a value nobody decided becoming a production default is the failure this
     * repository keeps finding.
     */
    { id: 'usage_limit_set', requirement: 'OPS-209', kind: 'mutate' },
    // OPS-207. Comments are typed by the shop to Lensmira, so they sit behind the audit like any read.
    { id: 'satisfaction_read', requirement: 'OPS-207', kind: 'read_metadata' },
    // Lifecycle, TEN-002a, TEN-007, Appendix M, OPS-202, OPS-203
    {
        id: 'provisioning_retry',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'provisioning_failed',
        to: 'provisioning',
    },
    {
        id: 'tenant_close',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'active',
        to: 'closing',
    },
    {
        id: 'tenant_enter_retention',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'closing',
        to: 'retention',
    },
    {
        id: 'tenant_purge',
        requirement: 'ADM-001',
        kind: 'lifecycle',
        from: 'retention',
        to: 'purged',
        requiresApproval: true,
    },
    { id: 'demo_reset', requirement: 'ADM-001', kind: 'mutate', demoOnly: true },
    // OPS-206. Feedback is the one deliberate shop-to-operator channel: the shop wrote it for ANUBRIS,
    // so listing it is a read of what the shop chose to send, not a read of shop records (Appendix N).
    // It is still shop-authored text, so it is not `read_metadata`, whose definition excludes it.
    { id: 'feedback_list', requirement: 'ADM-001', kind: 'read_shop_message' },
    { id: 'feedback_triage', requirement: 'ADM-001', kind: 'mutate' },
    /**
     * `OPS-002`. **Measured on production: `platform_operator` holds 0 rows.** `apps/admin` serves
     * eighteen routes, exactly one of them unauthenticated (`POST operator/login`), and that one
     * answers `UNAUTHENTICATED` because there is no account to match. Every one of the actions above
     * is behind a door with no key, `usage_limit_set` included, and the only file in this repository
     * that inserts an operator is a test fixture.
     *
     * So there are two halves and they are not the same job. The **first** operator is a bootstrap
     * and cannot be an operator action, because an allowlisted action needs an operator to perform
     * it: that is `tools/operator`, run on the server with credentials from the environment, the same
     * discipline `pnpm seed` uses. The **second and later** ones are this action, and offboarding is
     * its pair, because `offboarded_at` is likewise written by nothing.
     */
    { id: 'operator_create', requirement: 'OPS-002', kind: 'mutate' },
    { id: 'operator_offboard', requirement: 'OPS-002', kind: 'mutate' },
    /**
     * `AI-006`, and `../decisions/0037` is the whole argument. Mira is off in all fourteen
     * production shops because `ai_quota` holds no row anywhere and `quotaVerdict` returns
     * `unquoted` when the allowance is absent, which is the correct fail-closed branch and the only
     * state a shop can be in, since nothing can write the row.
     *
     * The operator's and not the shop's for the reason `usage_limit_set` gives: "by plan", and a plan
     * is not something a shop sets for itself. Not a duplicate of `usage_limit_set` either, which is
     * platform-side credits per tenant; this is per Appendix E `ai_task` calls a month, so a shop out
     * of prescription OCR does not thereby lose message drafting.
     */
    { id: 'ai_quota_set', requirement: 'AI-006', kind: 'mutate' },
];
const ACTION_INDEX = new Map(OPERATOR_ACTIONS.map((a) => [a.id, a]));
/** The registry lookup. Anything not written in `OPERATOR_ACTIONS` is `undefined`. */
export function operatorActionSpec(id) {
    return ACTION_INDEX.get(id);
}
// ---------------------------------------------------------------------------------------------
// What the operator may never read
// ---------------------------------------------------------------------------------------------
/**
 * Appendix N, `TEN-002`: the operator is not a tenant role and holds no Appendix B row. Every
 * permission, view and export included, is denied. A tenant record is reachable only through
 * `evaluateTenantDataRead` and an `ADM-005` consent.
 */
export function operatorHoldsPermission(permission) {
    // Unconditional, not a lookup in `OPERATOR_DENIED_PERMISSIONS`. A list-membership test answers
    // true for everything absent from the list, so a permission slug this build has never heard of,
    // one arriving already cast at the persistence boundary, would be granted to the operator.
    // On the most dangerous surface in the platform the default has to be the denial itself.
    void permission;
    return false;
}
/** Every Appendix B row, listed so a test can prove none of them is open to the operator. */
export const OPERATOR_DENIED_PERMISSIONS = PERMISSIONS;
export const DAY_MS = 86_400_000;
/** `TEN-007`: the shop can reverse a cancellation for 30 days. */
export const REVERSIBLE_DAYS = 30;
/** Appendix M: the cancelled database is kept 90 days, then dropped with recorded approval. */
export const DATABASE_KEPT_DAYS = 90;
const deny = (code) => ({ allowed: false, code });
/** Consent checks shared by the action path and the data-read path. */
function consentProblem(consent, tenantId, at, policy) {
    if (consent === undefined)
        return 'consent_required';
    if (consent.tenantId !== tenantId)
        return 'consent_tenant_mismatch';
    // Live at the instant it is granted, and not before it.
    if (at < consent.grantedAt)
        return 'consent_not_yet_valid';
    if (at >= consent.expiresAt)
        return 'consent_expired';
    if (consent.expiresAt - consent.grantedAt > policy.maxImpersonationMs) {
        return 'consent_window_too_long';
    }
    return undefined;
}
const MINUTE_MS = 60_000;
/**
 * What the shop must say to open support access: which Appendix B permissions, and for how many
 * whole minutes. `ADM-005`.
 *
 * **Neither has a default.** `ADM-005` says "time-boxed" and names no window, and says nothing of
 * scope, so a consent that did not name both would be the platform deciding what the shop agreed
 * to. The scope vocabulary is the `PERMISSIONS` registry because that is what
 * `evaluateTenantDataRead` compares against; no second list exists to invent.
 *
 * The ceiling is `policy.maxImpersonationMs`, the same figure `consentProblem` refuses a longer
 * window with. A grant that disagreed with the gate would write a consent that is refused the first
 * time anybody uses it, so the two share one number.
 */
export function checkConsentGrant(scope, durationMinutes, policy) {
    if (!Array.isArray(scope) || scope.length === 0)
        return { ok: false, problem: 'scope_required' };
    const known = new Set(PERMISSIONS);
    const unknown = scope.filter((entry) => typeof entry !== 'string' || !known.has(entry));
    if (unknown.length > 0) {
        return { ok: false, problem: 'scope_unknown_permission', unknown: unknown.map(String) };
    }
    if (typeof durationMinutes !== 'number' ||
        !Number.isInteger(durationMinutes) ||
        durationMinutes < 1) {
        return { ok: false, problem: 'duration_required' };
    }
    const durationMs = durationMinutes * MINUTE_MS;
    if (durationMs > policy.maxImpersonationMs)
        return { ok: false, problem: 'duration_too_long' };
    return {
        ok: true,
        scope: [...new Set(scope)],
        durationMs,
    };
}
/**
 * Whether the operator may perform `req.action` on `req.tenant` now. `ADM-001` to `ADM-009`.
 * Checks run cheapest and most fundamental first: allowlist, actor and reason (`ADM-009`), then the
 * action's own gates.
 */
export function evaluateOperatorAction(req, policy) {
    const spec = operatorActionSpec(req.action);
    if (spec === undefined)
        return deny('unknown_action');
    if (req.operatorId.trim() === '')
        return deny('actor_required');
    if (req.reason.trim() === '')
        return deny('reason_required');
    const { status } = req.tenant;
    if (spec.excludedStatuses?.includes(status))
        return deny('status_not_permitted');
    // Mutations and impersonation need a live subject. Metadata reads stay open on every status so a
    // purged tenant's history can still be inspected. Assumption, flagged: the spec is silent.
    if ((spec.kind === 'mutate' || spec.kind === 'impersonate') && status === 'purged') {
        return deny('status_not_permitted');
    }
    if (spec.kind === 'lifecycle') {
        // An entry missing either end is refused, not waved through.
        if (spec.from === undefined || spec.to === undefined)
            return deny('transition_not_allowed');
        if (status !== spec.from)
            return deny('transition_not_allowed');
    }
    if (spec.demoOnly && req.tenant.demo !== true)
        return deny('not_demo_tenant');
    if (spec.kind === 'impersonate') {
        // Impersonating needs a tenant a shop can consent for. Assumption, flagged: `active` only.
        if (status !== 'active')
            return deny('status_not_permitted');
        const problem = consentProblem(req.consent, req.tenant.id, req.at, policy);
        if (problem !== undefined)
            return deny(problem);
    }
    // `ADM-009` refuses a blank actor and a blank reason. A blank approver is no more a record.
    if (spec.requiresApproval && (req.approval?.approvedBy.trim() ?? '') === '') {
        return deny('approval_required');
    }
    if (spec.to === 'retention') {
        const reversible = reversibleProblem(req.tenant, req.at);
        if (reversible !== undefined)
            return deny(reversible);
    }
    if (spec.to === 'purged') {
        const purge = purgeProblem(req.tenant, req.at);
        if (purge !== undefined)
            return deny(purge);
    }
    return {
        allowed: true,
        ...(spec.to !== undefined ? { to: spec.to } : {}),
        entry: {
            actorKind: 'platform_operator',
            actorId: req.operatorId,
            action: spec.id,
            tenantId: req.tenant.id,
            reason: req.reason,
            at: req.at,
            writeToTenantAuditLog: spec.kind === 'impersonate',
        },
    };
}
/**
 * A read of shop data by the operator. Refused unless an `ADM-005` consent is live and names the
 * permission. This is the code form of "an operator must not be able to read shop data".
 */
export function evaluateTenantDataRead(permission, tenantId, consent, at, policy) {
    if (consent === undefined)
        return { allowed: false, code: 'no_standing_access' };
    const problem = consentProblem(consent, tenantId, at, policy);
    if (problem !== undefined)
        return { allowed: false, code: problem };
    if (!consent.scope.includes(permission))
        return { allowed: false, code: 'outside_consent_scope' };
    return { allowed: true };
}
/** Two clocks anchored on cancellation (`decisions/0005`). Calendar days are taken as 24 hours. */
export function retentionClocks(cancelledAt) {
    return {
        reversibleUntil: cancelledAt + REVERSIBLE_DAYS * DAY_MS,
        droppableFrom: cancelledAt + DATABASE_KEPT_DAYS * DAY_MS,
    };
}
/**
 * `TEN-007` gives the shop 30 days to reverse the cancellation itself, and `decisions/0005` puts
 * `restorable_until` between `closing_at` and `purge_after` for it.
 *
 * Assumption, flagged: the spec never says which status holds which clock. `closing` is taken as
 * the reversible phase and entering `retention` as the step that ends it, because `retention` is
 * the phase `TEN-007` describes as data that exists and a shop that cannot self-restore. Refusing
 * early is the fail-closed reading. The other way round, the step runs on day 1, a 30-day window
 * silently becomes a one-day window, and the shop learns about it by failing to restore.
 *
 * `TEN-007` also calls the step "scheduled". The gate holds whether a job or an operator asks.
 */
function reversibleProblem(tenant, at) {
    // No anchor, no way to show the 30 days ran, so the window is treated as still open.
    if (tenant.cancelledAt === undefined)
        return 'reversible_window_open';
    if (at < retentionClocks(tenant.cancelledAt).reversibleUntil)
        return 'reversible_window_open';
    return undefined;
}
/**
 * The gates on the drop itself. `retention` is not re-checked: `tenant_purge` declares
 * `from: 'retention'`, so every other status is already refused and a check here could never fire.
 */
function purgeProblem(tenant, at) {
    // Without an anchor the 90 days cannot be shown to have passed, so the drop is refused.
    if (tenant.cancelledAt === undefined)
        return 'retention_window_open';
    if (at < retentionClocks(tenant.cancelledAt).droppableFrom)
        return 'retention_window_open';
    if (!exportOffered(tenant))
        return 'export_not_offered';
    return undefined;
}
/** Splits a candidate set for a schema migration run. `ARC-020`. */
export function planMigrationRun(tenants, operatorId, reason, at, policy) {
    const eligible = [];
    const skipped = [];
    for (const tenant of tenants) {
        const d = evaluateOperatorAction({ operatorId, action: 'migration_run', reason, at, tenant }, policy);
        if (d.allowed)
            eligible.push(tenant.id);
        else
            skipped.push({ tenantId: tenant.id, code: d.code });
    }
    return { eligible, skipped };
}
// ---------------------------------------------------------------------------------------------
// OPS-200 to OPS-210, the rule halves
// ---------------------------------------------------------------------------------------------
/**
 * The plan a tenant is on. §14.2's three tiers, plus the two the lifecycle requirements name.
 *
 * `trial` is `OPS-201`'s: "If Paddle is unavailable at signup, the tenant is provisioned on trial".
 * `free` is `OPS-200`'s end state: "then downgrade to Free, never lockout (`C-03`)". §14.1 also
 * gives a free tier and a free year for the first twenty shops, so the two agree.
 *
 * Priced per region and deliberately not priced here: §14.2's heading says "to be priced per
 * region", so a plan is a name and an entitlement set, never an amount in this module.
 */
export const PLAN_TIERS = ['trial', 'free', 'starter', 'growth', 'business'];
/**
 * `OPS-200`'s end state. Named rather than written as a literal at each call, because "never
 * lockout" is the requirement and a downgrade target chosen per call site is how a lockout appears.
 */
export const DUNNING_DOWNGRADE_TIER = 'free';
export function dunningPhase(daysPastDue, policy) {
    const lastRetry = Math.max(0, ...policy.retryOnDays);
    if (daysPastDue <= lastRetry)
        return 'retrying';
    if (daysPastDue <= lastRetry + policy.graceDays)
        return 'grace';
    return 'downgraded_to_free';
}
export function dunningWarningDue(daysPastDue, policy) {
    return policy.warnOnDays.includes(daysPastDue);
}
/** `OPS-201`. Procedure half (reconcile later) is the `ADM-008` job. Onboarding is never blocked. */
export function signupBillingOutcome(paddleAvailable) {
    return { provision: true, plan: 'trial', reconcileLater: !paddleAvailable };
}
/** `OPS-202`, `TEN-007`: an export must have been offered before a purge. The format is unspecified. */
export function exportOffered(tenant) {
    return tenant.exportOfferedAt !== undefined;
}
/** `OPS-204`. Items are unspecified. Progress is counts, so nothing is rounded here (`MR-04`). */
export function checklistProgress(items) {
    return { done: items.filter((i) => i.done).length, total: items.length };
}
/** `OPS-208`, `C-05`: 20% of the subscription for 12 months. Amount and rounding are unspecified. */
export const REFERRAL_REWARD_MONTHS = 12;
export function referralRewardDue(monthsAlreadyRewarded) {
    return monthsAlreadyRewarded < REFERRAL_REWARD_MONTHS;
}
/** `OPS-209`. The six limited quantities the requirement names. */
export const USAGE_LIMIT_KINDS = [
    'customers',
    'catalogue_items',
    'storage',
    'ai_credits',
    'branches',
    'messages',
];
/**
 * `OPS-209`: "a warning before a limit is reached, never a surprise block". `warnAt` is unspecified
 * and must be below `limit`, otherwise there is no room for a warning and the call throws.
 */
export function usageStatus(used, limit, warnAt) {
    if (!(warnAt < limit))
        throw new RangeError('warnAt must be below limit (OPS-209)');
    if (used >= limit)
        return 'at_limit';
    return used >= warnAt ? 'warning' : 'ok';
}
/** `OPS-210`. The supported country list is unspecified. */
export function signupCountryOutcome(country, supported) {
    return supported.includes(country)
        ? { outcome: 'provision' }
        : { outcome: 'waitlist', explain: true };
}
//# sourceMappingURL=admin.js.map